import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import CustomError from '../../../../../utils/customError';
import { useSelector } from 'react-redux';
import * as Yup from "yup";
import { openCashDrawer, TestPrinterConnection, updatePrinterSettings } from '../../../services/profile.services';
import Swal from 'sweetalert2';
import { handleServerValidations } from '../../../../../utils/commonfunction';
import { LOADER_TIMEOUT_TIME, SWAL_SETTINGS, TABLE_SLIP_PRINT_MODES } from '../../../../../utils/Constants';
import { useDispatch } from 'react-redux';
import { addRestData } from '../../../../../redux/slices/restSettings';

const KITCHEN_TICKET_MODES = {
  SEPARATE: "separate",
  COMBINED: "combined",
};

const AUTO_PRINT_RECEIPT_FIELD = 'auto_print_receipt';

const PrinterSettings = () => {
  const { t } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
  const [connections, setConnections] = useState({});
  const restSettingDetails = useSelector((state) => state?.restSettings?.data);
  const [cashierLoader, setCashierLoader] = useState(false);
  const [kitchenLoader, setKitchenLoader] = useState(false);
  const [drawerLoader, setDrawerLoader] = useState(false);


  const Dispatch = useDispatch();

  const settingsForm = useFormik({
    initialValues: {
      kitchen_printer_ip: restSettingDetails ? restSettingDetails?.kitchen_printer_ip : "",
      cashier_printer_ip: restSettingDetails ? restSettingDetails?.cashier_printer_ip : "",
      hall_printer_ip: restSettingDetails ? restSettingDetails?.hall_printer_ip : "",
      kitchen_ticket_mode: restSettingDetails?.kitchen_ticket_mode ?? KITCHEN_TICKET_MODES.SEPARATE,
      auto_print_receipt: restSettingDetails?.auto_print_receipt ?? true,
      print_language: restSettingDetails ? (restSettingDetails?.print_language ?? "") : "en",
      table_slip_print_mode: restSettingDetails?.table_slip_print_mode ?? TABLE_SLIP_PRINT_MODES.PER_ORDER,
      // Empty = inherit the restaurant-wide print_language; do NOT auto-fill from it,
      // which would freeze the current default into an explicit per-role override.
      kitchen_printer_language: restSettingDetails?.kitchen_printer_language ?? "",
      cashier_printer_language: restSettingDetails?.cashier_printer_language ?? "",
      hall_printer_language: restSettingDetails?.hall_printer_language ?? "",
      cashier_drawer_enabled: restSettingDetails?.cashier_drawer_enabled === true,
    },
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      kitchen_printer_ip: Yup.string()
        .matches(
          /^(?:\d{1,3}\.){3}\d{1,3}$/,
          "Invalid IP address. Use a valid IPv4 format (e.g., 192.168.1.1)",
          {excludeEmptyString: true},
        ),
      cashier_printer_ip: Yup.string().matches(
          /^(?:\d{1,3}\.){3}\d{1,3}$/,
          "Invalid IP address. Use a valid IPv4 format (e.g., 192.168.1.1)",
          {excludeEmptyString: true},
        ),
      hall_printer_ip: Yup.string()
        .matches(
          /^(?:\d{1,3}\.){3}\d{1,3}$/,
          "Invalid IP address. Use a valid IPv4 format (e.g., 192.168.1.1)",
          {excludeEmptyString: true},
        ),
      kitchen_ticket_mode: Yup.string().oneOf(Object.values(KITCHEN_TICKET_MODES)).required(),
      auto_print_receipt: Yup.boolean(),
      print_language: Yup.string()
        .required(t('print_language_error')),
      table_slip_print_mode: Yup.string()
        .oneOf(Object.values(TABLE_SLIP_PRINT_MODES))
        .required(),
      kitchen_printer_language: Yup.string().oneOf(["", "en", "ja"]),
      cashier_printer_language: Yup.string().oneOf(["", "en", "ja"]),
      hall_printer_language: Yup.string().oneOf(["", "en", "ja"]),
      cashier_drawer_enabled: Yup.boolean(),
    }),
    onSubmit: (values) => {
      setAddSubmissionLoader(true);
      updatePrinterSettings(values).then((response) => {
        setAddSubmissionLoader(false);
        if (response.success) {
          // Only commit the form values to Redux once the server accepted them,
          // otherwise a rejected save would show unsaved values as persisted.
          let updatePrinterData = { ...restSettingDetails };
          updatePrinterData.kitchen_printer_ip = values.kitchen_printer_ip;
          updatePrinterData.cashier_printer_ip = values.cashier_printer_ip;
          updatePrinterData.hall_printer_ip = values.hall_printer_ip;
          updatePrinterData.kitchen_ticket_mode = values.kitchen_ticket_mode;
          updatePrinterData.auto_print_receipt = values.auto_print_receipt;
          updatePrinterData.print_language = values.print_language;
          updatePrinterData.table_slip_print_mode = values.table_slip_print_mode;
          updatePrinterData.kitchen_printer_language = values.kitchen_printer_language;
          updatePrinterData.cashier_printer_language = values.cashier_printer_language;
          updatePrinterData.hall_printer_language = values.hall_printer_language;
          updatePrinterData.cashier_drawer_enabled = values.cashier_drawer_enabled;
          Dispatch(addRestData(updatePrinterData));
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });

        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      }).catch((error) => {
        setAddSubmissionLoader(false);
        console.error("Error updating charges", error);
      });
    },
  });
  useEffect(() => {
    if (restSettingDetails && typeof restSettingDetails === "object" && Object.keys(restSettingDetails).length) {
      setDataLoaded(true);
    }
  }, [restSettingDetails]);


  const checkPrinterConnection = (key) => {
    if (key === "kitchen_printer") {
      setKitchenLoader(true)
    }
    else {
      setCashierLoader(true)
    }
    const IP = settingsForm?.values[key + "_ip"];
    console.log(IP);

    TestPrinterConnection({ printerIp: IP })
      .then((response) => {
        if (response.success) {
          let tempConnections = { ...connections };
          tempConnections[key] = {
            status: true,
            message: "Connected",
          };
          setConnections(tempConnections); // ✅ Ensure state update here
          if (key === "kitchen_printer") {
            setTimeout(() => {
              setKitchenLoader(false);
            }, LOADER_TIMEOUT_TIME);

          }
          else {
            setTimeout(() => {
              setCashierLoader(false);
            }, LOADER_TIMEOUT_TIME);

          }
        }
        else {
          let tempConnections = { ...connections };
          tempConnections[key] = {
            status: false,
            message: "failed",
          };
          setConnections(tempConnections);
          if (key === "kitchen_printer") {
            setTimeout(() => {
              setKitchenLoader(false);
            }, LOADER_TIMEOUT_TIME);

          }
          else {
            setTimeout(() => {
              setCashierLoader(false);
            }, LOADER_TIMEOUT_TIME);

          }
        }

      })
      .catch((err) => {
        let tempConnections = { ...connections };
        tempConnections[key] = {
          status: false,
          message: err?.message,
        };
        setConnections(tempConnections);
        if (key === "kitchen_printer") {
          setTimeout(() => {
            setKitchenLoader(false);
          }, LOADER_TIMEOUT_TIME);

        }
        else {
          setTimeout(() => {
            setCashierLoader(false);
          }, LOADER_TIMEOUT_TIME);

        }
      });
  };

  const handleOpenDrawer = async () => {
    if (settingsForm.dirty) {
      Swal.fire({
        icon: 'warning',
        text: t('msg_save_drawer_settings_first'),
        ...SWAL_SETTINGS,
      });
      return;
    }
    const passwordResult = await Swal.fire({
      title: t('btn_open_cash_drawer'),
      input: 'password',
      inputLabel: t('text_reenter_password_for_confirm'),
      inputAttributes: {autocomplete: 'current-password'},
      showCancelButton: true,
      confirmButtonText: t('btn_open_cash_drawer'),
      ...SWAL_SETTINGS,
      inputValidator: (value) => value ? undefined : t('validation_password_required'),
    });
    if (!passwordResult.value) return;
    setDrawerLoader(true);
    try {
      const response = await openCashDrawer(passwordResult.value);
      Swal.fire({
        icon: response?.success ? 'success' : 'error',
        text: response?.success ? t('msg_drawer_request_queued') : handleServerValidations(response),
        ...SWAL_SETTINGS,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        text: error?.response?.data?.message || t('msg_drawer_request_failed'),
        ...SWAL_SETTINGS,
      });
    } finally {
      setDrawerLoader(false);
    }
  };


  const RenderConnectionStatus = ({ connectionKey, loader }) => {
    return <>
      <button type='button' className={"btn btn-primary mx-3" + (loader ? " btn-loader" : "")} onClick={() => { checkPrinterConnection(connectionKey) }} disabled={loader}>
        Test Connection</button>
      {
        connections && connections[connectionKey] ?
          <>
            {
              connections[connectionKey]?.status ?
                <div className='text-success d-flex align-items-center'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-rosette-discount-check"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12.01 2.011a3.2 3.2 0 0 1 2.113 .797l.154 .145l.698 .698a1.2 1.2 0 0 0 .71 .341l.135 .008h1a3.2 3.2 0 0 1 3.195 3.018l.005 .182v1c0 .27 .092 .533 .258 .743l.09 .1l.697 .698a3.2 3.2 0 0 1 .147 4.382l-.145 .154l-.698 .698a1.2 1.2 0 0 0 -.341 .71l-.008 .135v1a3.2 3.2 0 0 1 -3.018 3.195l-.182 .005h-1a1.2 1.2 0 0 0 -.743 .258l-.1 .09l-.698 .697a3.2 3.2 0 0 1 -4.382 .147l-.154 -.145l-.698 -.698a1.2 1.2 0 0 0 -.71 -.341l-.135 -.008h-1a3.2 3.2 0 0 1 -3.195 -3.018l-.005 -.182v-1a1.2 1.2 0 0 0 -.258 -.743l-.09 -.1l-.697 -.698a3.2 3.2 0 0 1 -.147 -4.382l.145 -.154l.698 -.698a1.2 1.2 0 0 0 .341 -.71l.008 -.135v-1l.005 -.182a3.2 3.2 0 0 1 3.013 -3.013l.182 -.005h1a1.2 1.2 0 0 0 .743 -.258l.1 -.09l.698 -.697a3.2 3.2 0 0 1 2.269 -.944zm3.697 7.282a1 1 0 0 0 -1.414 0l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.32 1.497l2 2l.094 .083a1 1 0 0 0 1.32 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" /></svg>
                  <span className='ms-2 printer-settings-status-label'>{t('label_connected')}</span>
                </div>
                :
                <div className='text-danger d-flex align-items-center'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-square-x"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M19 2h-14a3 3 0 0 0 -3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3 -3v-14a3 3 0 0 0 -3 -3zm-9.387 6.21l.094 .083l2.293 2.292l2.293 -2.292a1 1 0 0 1 1.497 1.32l-.083 .094l-2.292 2.293l2.292 2.293a1 1 0 0 1 -1.32 1.497l-.094 -.083l-2.293 -2.292l-2.293 2.292a1 1 0 0 1 -1.497 -1.32l.083 -.094l2.292 -2.293l-2.292 -2.293a1 1 0 0 1 1.32 -1.497z" /></svg>
                  <span className='ms-2 printer-settings-status-label'>{t('label_error')}</span>
                </div>
            }
          </>
          : null
      }
    </>
  }

  return (
    <main id='main-content' className='printer-settings-page'>
      <div className='inner-container'>
        <div className='page-header d-flex align-items-center'>
          <h4 className='heading-title-text'>{t("heading_printer_settings")}</h4>
        </div>
        {
          !dataLoaded ? (<div className="service-charges-box">
            <div className="card p-2">
              <Skeleton className="m-0" height={50} count={2} />
            </div>
          </div>) : (
            <div className="service-charges-box">
              <form onSubmit={settingsForm.handleSubmit}>
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex gap-3">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className='form-label' htmlFor='kitchen_printer_ip'>{t('label_kitchen_printer_ip')}</label>
                          <div className='d-flex w-100 gap-3'>
                            <div>
                              <input type="text" className="form-control printer-settings-min-width" id='kitchen_printer_ip' name='kitchen_printer_ip'
                                value={settingsForm?.values?.kitchen_printer_ip} onChange={settingsForm.handleChange} onBlur={settingsForm.handleBlur} placeholder={"192.XXX.XXX.XXX"} />
                              <CustomError className='text-danger' name="kitchen_printer_ip" form={settingsForm} />
                            </div>
                            <div>
                              <select
                                id="kitchen_printer_language"
                                name="kitchen_printer_language"
                                className="form-control"
                                value={settingsForm.values.kitchen_printer_language}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                title={t('label_printer_language_override')}
                              >
                                <option value="">{t('label_inherit_default')}</option>
                                <option value="en">English</option>
                                <option value="ja">Japanese</option>
                              </select>
                              <CustomError className='text-danger' name="kitchen_printer_language" form={settingsForm} />
                            </div>
                            {/* <RenderConnectionStatus connectionKey="kitchen_printer" loader={kitchenLoader} /> */}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-3 mt-4">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className='form-label' htmlFor='cashier_printer_ip'>{t('label_cashier_printer_ip')}</label>
                          <div className='d-flex w-100 gap-3'>
                            <div>
                              <input type="text" id='cashier_printer_ip' className="form-control printer-settings-min-width" name='cashier_printer_ip'
                                value={settingsForm?.values?.cashier_printer_ip} onChange={settingsForm.handleChange} onBlur={settingsForm.handleBlur} placeholder={"192.XXX.XXX.XXX"} />
                              <CustomError className='text-danger' name="cashier_printer_ip" form={settingsForm} />
                            </div>
                            <div>
                              <select
                                id="cashier_printer_language"
                                name="cashier_printer_language"
                                className="form-control"
                                value={settingsForm.values.cashier_printer_language}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                title={t('label_printer_language_override')}
                              >
                                <option value="">{t('label_inherit_default')}</option>
                                <option value="en">English</option>
                                <option value="ja">Japanese</option>
                              </select>
                              <CustomError className='text-danger' name="cashier_printer_language" form={settingsForm} />
                            </div>
                            {/* <RenderConnectionStatus connectionKey="cashier_printer" loader={cashierLoader}/> */}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 mt-4">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input cp"
                          type="checkbox"
                          id="cashier_drawer_enabled"
                          name="cashier_drawer_enabled"
                          checked={settingsForm.values.cashier_drawer_enabled}
                          onChange={settingsForm.handleChange}
                          onBlur={settingsForm.handleBlur}
                        />
                        <label className="form-check-label cp" htmlFor="cashier_drawer_enabled">
                          {t('label_cash_drawer_enabled')}
                        </label>
                      </div>
                      <button
                        type="button"
                        className={"btn btn-secondary" + (drawerLoader ? " btn-loader" : "")}
                        disabled={drawerLoader || !settingsForm.values.cashier_drawer_enabled}
                        onClick={handleOpenDrawer}
                      >
                        {t('btn_open_cash_drawer')}
                      </button>
                    </div>

                    <div className="d-flex gap-3 mt-4">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className='form-label' htmlFor='hall_printer_ip'>{t('hall_cashier_printer_ip')}</label>
                          <div className='d-flex w-100 gap-3'>
                            <div>
                              <input type="text" id='hall_printer_ip' className="form-control printer-settings-min-width" name='hall_printer_ip'
                                value={settingsForm?.values?.hall_printer_ip} onChange={settingsForm.handleChange} onBlur={settingsForm.handleBlur} placeholder={"192.XXX.XXX.XXX"} />
                              <CustomError className='text-danger' name="hall_printer_ip" form={settingsForm} />
                            </div>
                            <div>
                              <select
                                id="hall_printer_language"
                                name="hall_printer_language"
                                className="form-control"
                                value={settingsForm.values.hall_printer_language}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                title={t('label_printer_language_override')}
                              >
                                <option value="">{t('label_inherit_default')}</option>
                                <option value="en">English</option>
                                <option value="ja">Japanese</option>
                              </select>
                              <CustomError className='text-danger' name="hall_printer_language" form={settingsForm} />
                            </div>
                            {/* <RenderConnectionStatus connectionKey="cashier_printer" loader={cashierLoader}/> */}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 mt-4 printer-mode-section">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className="form-label">
                            {t("label_kitchen_ticket_mode")}
                          </label>
                          <div className="printer-settings-mode-options mt-2">
                            <div className="form-check">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="kitchen_ticket_mode_separate"
                                name="kitchen_ticket_mode"
                                value={KITCHEN_TICKET_MODES.SEPARATE}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values.kitchen_ticket_mode === KITCHEN_TICKET_MODES.SEPARATE}
                              />
                              <label className="form-check-label cp" htmlFor="kitchen_ticket_mode_separate">
                                {t("label_kitchen_ticket_mode_separate")}
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="kitchen_ticket_mode_combined"
                                name="kitchen_ticket_mode"
                                value={KITCHEN_TICKET_MODES.COMBINED}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values.kitchen_ticket_mode === KITCHEN_TICKET_MODES.COMBINED}
                              />
                              <label className="form-check-label cp" htmlFor="kitchen_ticket_mode_combined">
                                {t("label_kitchen_ticket_mode_combined")}
                              </label>
                            </div>
                          </div>
                          <CustomError className='text-danger' name="kitchen_ticket_mode" form={settingsForm} />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 mt-4 printer-mode-section">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className="form-label">
                            {t("label_table_slip_print_mode")}
                          </label>
                          <div className="printer-settings-mode-options d-flex gap-4 flex-wrap mt-2">
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="table_slip_print_mode_per_order"
                                name="table_slip_print_mode"
                                value={TABLE_SLIP_PRINT_MODES.PER_ORDER}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values.table_slip_print_mode === TABLE_SLIP_PRINT_MODES.PER_ORDER}
                              />
                              <label className="form-check-label cp" htmlFor="table_slip_print_mode_per_order">
                                {t("label_table_slip_print_mode_per_order")}
                              </label>
                            </div>
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="table_slip_print_mode_consolidated"
                                name="table_slip_print_mode"
                                value={TABLE_SLIP_PRINT_MODES.CONSOLIDATED_AT_END}
                                onChange={settingsForm.handleChange}
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values.table_slip_print_mode === TABLE_SLIP_PRINT_MODES.CONSOLIDATED_AT_END}
                              />
                              <label className="form-check-label cp" htmlFor="table_slip_print_mode_consolidated">
                                {t("label_table_slip_print_mode_consolidated")}
                              </label>
                            </div>
                          </div>
                          <CustomError className='text-danger' name="table_slip_print_mode" form={settingsForm} />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 mt-4">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group w-100">
                          <label className="form-label">
                            {t("label_receipt_printing")}
                          </label>
                          <div className="printer-settings-mode-options mt-2">
                            <div className="form-check">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="auto_print_receipt_on"
                                name={AUTO_PRINT_RECEIPT_FIELD}
                                onChange={() =>
                                  settingsForm.setFieldValue(AUTO_PRINT_RECEIPT_FIELD, true)
                                }
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values[AUTO_PRINT_RECEIPT_FIELD] === true}
                              />
                              <label className="form-check-label cp" htmlFor="auto_print_receipt_on">
                                {t("label_print_receipt_automatically")}
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input cp"
                                type="radio"
                                id="auto_print_receipt_off"
                                name={AUTO_PRINT_RECEIPT_FIELD}
                                onChange={() =>
                                  settingsForm.setFieldValue(AUTO_PRINT_RECEIPT_FIELD, false)
                                }
                                onBlur={settingsForm.handleBlur}
                                checked={settingsForm.values[AUTO_PRINT_RECEIPT_FIELD] === false}
                              />
                              <label className="form-check-label cp" htmlFor="auto_print_receipt_off">
                                {t("label_print_receipt_manually")}
                              </label>
                            </div>
                          </div>
                          <CustomError className='text-danger' name={AUTO_PRINT_RECEIPT_FIELD} form={settingsForm} />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-3 mt-4">
                      <div className="d-flex gap-3 w-100">
                        <div className="form-group printer-settings-min-width">
                          <label className="form-label"
                            htmlFor="print_language">
                            {t("label_print_language")}
                          </label>

                          <select
                            id="print_language"
                            name="print_language"
                            className="form-control"

                            value={settingsForm.values.print_language}
                            onChange={settingsForm.handleChange}
                            onBlur={settingsForm.handleBlur}
                          >
                            <option value="en">English</option>
                            <option value="ja">Japanese</option>
                          </select>

                          <CustomError className="text-danger"
                            name="print_language" form={settingsForm} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button type="submit" className={"btn btn-primary mt-4" + (addSubmissionLoader ? " btn-loader" : "")}>
                  {t('btn_update')}
                </button>
              </form>
            </div>
          )
        }
      </div>
    </main>
  );
};

export default PrinterSettings;
